<template>
    <section
        v-if="queue.items.length || queue.skipped"
        class="fixed bottom-4 right-4 z-40 flex max-h-[70vh] w-[calc(100vw-2rem)] max-w-md flex-col overflow-hidden rounded-lg border border-background-accent-dark bg-background shadow-lg"
        :aria-label="t('label')"
    >
        <header class="flex items-center gap-3 border-b border-background-accent-dark px-4 py-3">
            <div class="rounded-full bg-primary/10 p-2 text-primary">
                <UploadIcon class="h-4 w-4 fill-current"/>
            </div>
            <div class="min-w-0 grow">
                <p class="truncate font-semibold text-text" aria-live="polite">{{ summary }}</p>
                <div class="mt-1 h-1.5 w-full overflow-hidden rounded-md bg-primary-light">
                    <div
                        class="h-full rounded-md bg-primary transition-[width]"
                        :class="{ 'bg-destructive!': !queue.isActive && queue.totals.failed }"
                        :style="`width: ${ queue.totals.progress }%`"
                    ></div>
                </div>
            </div>
            <yButton
                small
                type="primary"
                text-only
                icon="CaretIcon"
                :class="{ 'rotate-180': !collapsed }"
                :aria-expanded="!collapsed"
                :aria-label="collapsed ? t('expand') : t('collapse')"
                @click="collapsed = !collapsed"
            />
            <yButton
                v-if="!queue.isActive"
                small
                type="primary"
                text-only
                icon="CloseIcon"
                :aria-label="t('close')"
                @click="queue.clearFinished()"
            />
        </header>

        <p v-if="queue.skipped && !collapsed" class="border-b border-background-accent-dark px-4 py-2 text-xs text-text-light">
            {{ t('skipped', { count: queue.skipped }, queue.skipped) }}
        </p>

        <ul v-show="!collapsed" class="flex flex-col divide-y divide-background-accent-dark overflow-y-auto">
            <li v-for="item in queue.items" :key="item.id" class="flex flex-col gap-1.5 px-4 py-3">
                <div class="flex items-center gap-2">
                    <div class="h-4 w-4 shrink-0">
                        <SpinnerIcon v-if="item.status === 'uploading'" class="h-4 w-4 animate-spin fill-primary"/>
                        <checkmark-icon v-else-if="item.status === 'done'" class="h-4 w-4 fill-green-400"/>
                        <CloseIcon v-else-if="item.status === 'error'" class="h-4 w-4 fill-destructive"/>
                        <MinusIcon v-else class="h-4 w-4 fill-text-light"/>
                    </div>
                    <NuxtLink
                        v-if="item.contentId"
                        :to="localePath(`${ UPLOAD_TARGETS[item.kind].gallery }/${ item.contentId }`)"
                        class="min-w-0 grow truncate text-sm text-primary hover:underline"
                    >
                        {{ item.name }}
                    </NuxtLink>
                    <span v-else class="min-w-0 grow truncate text-sm text-text" :class="{ 'text-text-light line-through': item.status === 'cancelled' }">
                        {{ item.name }}
                    </span>
                    <span class="shrink-0 text-xs tabular-nums text-text-light">{{ formatUploadBytes(item.size) }}</span>
                    <yButton
                        v-if="item.status === 'queued' || item.status === 'uploading'"
                        small
                        type="destructive"
                        text-only
                        class="h-7!"
                        @click="queue.cancel(item.id)"
                    >
                        {{ t('cancel') }}
                    </yButton>
                    <yButton
                        v-else-if="canRetry(item)"
                        small
                        type="primary"
                        text-only
                        class="h-7!"
                        @click="queue.retry(item.id)"
                    >
                        {{ t('retry') }}
                    </yButton>
                    <yButton
                        v-if="item.status !== 'queued' && item.status !== 'uploading'"
                        small
                        type="basic"
                        text-only
                        icon="CloseIcon"
                        class="h-7!"
                        :aria-label="t('remove', { name: item.name })"
                        @click="queue.remove(item.id)"
                    />
                </div>
                <template v-if="item.status === 'uploading'">
                    <div class="h-1 w-full overflow-hidden rounded-md bg-primary-light">
                        <div class="h-full rounded-md bg-primary transition-[width]" :style="`width: ${ item.progress }%`"></div>
                    </div>
                    <UploadSpeed v-if="item.speed" :stats="item.speed" class="self-start"/>
                </template>
                <p v-else-if="item.status === 'error'" class="text-xs text-destructive" role="alert">
                    {{ errorMessage(item) }}
                </p>
                <p v-else-if="item.status === 'queued'" class="text-xs text-text-light">{{ t('waiting') }}</p>
                <p v-else-if="item.status === 'done'" class="text-xs text-text-light">
                    {{ item.kind === 'video' ? t('doneProcessing') : t('done') }}
                </p>
            </li>
        </ul>

        <footer v-if="queue.isActive && !collapsed" class="flex justify-between gap-2 border-t border-background-accent-dark px-4 py-2">
            <p class="text-xs text-text-light">{{ t('keepTabOpen') }}</p>
            <yButton small type="destructive" text-only class="h-7! shrink-0" @click="queue.cancelAll()">
                {{ t('cancelAll') }}
            </yButton>
        </footer>
    </section>
</template>

<script setup lang="ts">
import { useEventListener } from '@vueuse/core'

const queue = useUploadQueueStore()
const localePath = useLocalePath()
const { t } = useI18n({ useScope: 'local' })

const collapsed = ref(false)

const summary = computed(() => {
    const { done, failed, count } = queue.totals
    if (queue.isActive) return t('uploading', { current: Math.min(done + failed + 1, count), count })
    if (failed) return t('finishedWithErrors', { done, failed })
    return t('finished', { count: done }, done)
})

const canRetry = (item: UploadItem) => (item.status === 'error' || item.status === 'cancelled')
    && item.error?.code !== 'unsupported' && item.error?.code !== 'tooLarge'

const errorMessage = (item: UploadItem) => {
    const error = item.error
    if (!error) return t('uploadError')
    switch (error.code) {
        case 'tooLarge':
            return error.message
        case 'unsupported':
            return t(`unsupported.${ item.kind }`)
        case 'network':
            return t('networkError')
        case 'http':
            return error.status === 413 ? t('tooLarge') : error.message || t('uploadError')
    }
}

// Closing or reloading the tab would drop every upload still in the browser, so ask first.
useEventListener('beforeunload', (event: BeforeUnloadEvent) => {
    if (!queue.isActive) return
    event.preventDefault()
    event.returnValue = ''
})
</script>

<i18n lang="yaml">
en:
    label: Uploads
    uploading: "Uploading file {current} of {count}"
    finished: "{count} file uploaded | {count} files uploaded"
    finishedWithErrors: "{done} uploaded, {failed} failed"
    expand: Show uploads
    collapse: Hide uploads
    close: Close upload overview
    cancel: Cancel
    cancelAll: Cancel all
    retry: Retry
    remove: "Remove {name} from the list"
    waiting: Waiting…
    done: Uploaded.
    doneProcessing: Uploaded, processing continues on the server.
    keepTabOpen: You can keep working in the CMS, but keep this tab open until uploads finish.
    uploadError: The file could not be uploaded. Please try again.
    networkError: The connection was lost. Please try again.
    unsupported:
        image: This file is not a supported image.
        video: This file is not a supported video.
        audio: This file is not a supported audio file.
        file: This file type is not supported.
    tooLarge: This file is too large.
    skipped: "{count} file from the folder was skipped, as this gallery doesn't accept it. | {count} files from the folder were skipped, as this gallery doesn't accept them."
nl:
    label: Uploads
    uploading: "Bestand {current} van {count} uploaden"
    finished: "{count} bestand geüpload | {count} bestanden geüpload"
    finishedWithErrors: "{done} geüpload, {failed} mislukt"
    expand: Uploads tonen
    collapse: Uploads verbergen
    close: Uploadoverzicht sluiten
    cancel: Annuleren
    cancelAll: Alles annuleren
    retry: Opnieuw
    remove: "{name} uit de lijst verwijderen"
    waiting: Wachten…
    done: Geüpload.
    doneProcessing: Geüpload, de verwerking gaat verder op de server.
    keepTabOpen: Je kunt verder werken in het CMS, maar houd dit tabblad open tot de uploads klaar zijn.
    uploadError: Het bestand kon niet worden geüpload. Probeer het opnieuw.
    networkError: De verbinding is verbroken. Probeer het opnieuw.
    unsupported:
        image: Dit bestand is geen ondersteunde afbeelding.
        video: Dit bestand is geen ondersteunde video.
        audio: Dit bestand is geen ondersteund audiobestand.
        file: Dit bestandstype wordt niet ondersteund.
    tooLarge: Dit bestand is te groot.
    skipped: "{count} bestand uit de map is overgeslagen, omdat deze galerij het niet accepteert. | {count} bestanden uit de map zijn overgeslagen, omdat deze galerij ze niet accepteert."
</i18n>
